import React from 'react';
import { EntryActionTypes, StatusTypes } from '@brighterionai/br-ui';
import { useTranslation } from 'react-i18next';
import { getElapsedTime } from '@brighterion/utils/date';
import {
    IMPORT_EXPORT_PROCESS_TYPE,
    IMPORT_EXPORT_STATUS,
    SIDE_PANEL_CONTENT_TYPE
} from '@brighterion/utils/constants';
import { useAppStore } from 'Store';
import { useFileImportStatus } from 'Hooks/useFileImportStatus';

const useGetSidePanelContentData = () => {
    const { t } = useTranslation();
    useFileImportStatus();
    const { inProgress, completed } = useAppStore();
    const importExportData = {
        inProgress,
        completed
    };
    const now = new Date();

    const listDataTransformer = (dataItem: ImportExportAPIResponse, isInProgress?: boolean) => {
        const dataItemStatus = { type: dataItem.status as keyof typeof StatusTypes, label: t(dataItem.status) };

        let result: SidePanelAccordionItemContentCardData = {
            id: dataItem.fileMetadataId as string,
            entryName: dataItem.listName,
            entryActionType: IMPORT_EXPORT_PROCESS_TYPE.IMPORT,
            fileName: dataItem.fileName,
            actionDetails: {
                icon: 'arrow-up-outline',
                description: t('Import entries from CSV')
            },
            status: dataItemStatus,
            creationDetail: t(`Started By: ${dataItem.createdBy}`),
            timeDetail: '',
            progressDetails: dataItem.progressDetails
        };
        if (isInProgress) {
            const createdAt = new Date(dataItem.createdAt);
            const timeDetail = getElapsedTime(now, createdAt);

            result = {
                ...result,
                timeDetail: t(`${timeDetail.elapsedTime}${timeDetail.format} ago`)
            };
        } else {
            const expiryDate = new Date(dataItem.expiredIn ?? 0);
            const timeDetail = getElapsedTime(expiryDate, now);
            const isFinished = dataItem.status === IMPORT_EXPORT_STATUS.FINISHED;
            const actionText = isFinished && dataItem.pendingChanges ? t('View pending changes') : undefined;
            const pendingActionDetails =
                isFinished && dataItem.pendingChanges
                    ? {
                          id: Number(dataItem.fileMetadataId),
                          type: EntryActionTypes.IMPORT,
                          status: dataItem.status,
                          listName: dataItem.listName,
                          revisionId: dataItem.revisionId,
                          listId: dataItem.listId
                      }
                    : undefined;

            const commonResult = {
                ...result,
                timeDetail: t(`Expires In ${timeDetail.elapsedTime}${timeDetail.format}`),
                actionText,
                pendingActionDetails
            };
            result = { ...commonResult };
        }

        return result;
    };

    const getData = React.useCallback(
        (type: SIDE_PANEL_CONTENT_TYPE) => {
            const contentData =
                importExportData?.[type]?.filter((item: any) => {
                    const expiryDate = new Date(item.expiredIn ?? 0);
                    const timeDetail = getElapsedTime(expiryDate, now);
                    if (timeDetail.elapsedTime < 0) {
                        return false;
                    }
                    return true;
                }) ?? [];
            return contentData.map((item) => listDataTransformer(item, type === SIDE_PANEL_CONTENT_TYPE.IN_PROGRESS));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [importExportData]
    );
    return getData;
};

export default useGetSidePanelContentData;
